import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { CommonQuestions } from "../components/journey/CommonQuestions";
import {
	CurrentTasks,
	type NewTaskInput,
} from "../components/journey/CurrentTasks";
import { JourneyBookingModal } from "../components/journey/JourneyBookingModal";
import { JourneyHeader } from "../components/journey/JourneyHeader";
import { JourneyShellSkeleton } from "../components/journey/JourneyShellSkeleton";
import { StepActionButtons } from "../components/journey/StepActionButtons";
import { StepDetail } from "../components/journey/StepDetail";
import { StepHighlights } from "../components/journey/StepHighlights";
import { StepPills } from "../components/journey/StepPills";
import { FullPageErrorState } from "../components/ui/FullPageErrorState";
import { useChatActions } from "../contexts/chat-actions-context";
import { useJourney } from "../hooks/useJourney";
import { useTrackPageView } from "../hooks/useTrackPageView";
import { useUser } from "../hooks/useUser";
import { useUserProviders } from "../hooks/useUserProviders";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import type { ScheduledMeeting } from "../types/booking";
import {
	JOURNEY_TYPES,
	type JourneyTask,
	type JourneyType,
} from "../types/journey";
import styles from "./Journey.module.css";

function isJourneyType(value: string | undefined): value is JourneyType {
	return (
		value !== undefined && (JOURNEY_TYPES as readonly string[]).includes(value)
	);
}

export function Journey() {
	const { t, direction, locale } = useClientTranslations("journey");
	const navigate = useNavigate();
	const { setPrefillQuestion } = useChatActions();

	const { data: user } = useUser();
	const { data: providerData } = useUserProviders();
	const assignedProviderId = providerData?.providers?.[0]?.providerUserId;
	// Profile stores fertilityJourneyTypes as an array (multi-select). Prefer the
	// first entry whose value matches a known JourneyType. Fall back to the legacy
	// singular field, then to "ivf" as a final default.
	const journeyTypes = user?.details?.fertilityJourneyTypes;
	const firstMatch = Array.isArray(journeyTypes)
		? journeyTypes
				.map((t) => (typeof t === "string" ? t.toLowerCase() : ""))
				.find((t) => isJourneyType(t))
		: undefined;
	const legacyType = user?.details?.fertilityJourneyType?.toLowerCase();
	const resolvedType =
		firstMatch ?? (isJourneyType(legacyType) ? legacyType : undefined);
	const treatmentType: JourneyType = resolvedType ?? "ivf";

	const {
		data: journey,
		isLoading,
		isError,
		refetch,
	} = useJourney(treatmentType, { locale });

	const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
	const initializedJourneyIdRef = useRef<string | null>(null);
	const [patientTasks, setPatientTasks] = useState<JourneyTask[]>([]);

	useEffect(() => {
		if (!journey) return;
		// Reset selection when the journey itself changes (e.g., IVF → IUI). The
		// previous step id no longer exists in the new step list.
		if (initializedJourneyIdRef.current === journey.id) return;
		const current = journey.steps.find((s) => s.status === "current");
		const fallback = journey.steps[0];
		setSelectedStepId(current?.id ?? fallback?.id ?? null);
		initializedJourneyIdRef.current = journey.id;
	}, [journey]);

	const showSkeleton = isLoading && !journey;
	const currentStepIndex = journey
		? journey.steps.findIndex((s) => s.status === "current")
		: -1;
	const effectiveSelectedId = selectedStepId ?? journey?.steps[0]?.id ?? "";
	const selectedStep =
		journey?.steps.find((s) => s.id === effectiveSelectedId) ?? null;
	const mergedTasks: JourneyTask[] = selectedStep
		? [...patientTasks, ...selectedStep.tasks]
		: [];

	const handleAddTask = (next: NewTaskInput) => {
		const newTask: JourneyTask = {
			id: `local-${Date.now()}`,
			title: next.title,
			description: next.description,
			category: next.category,
			deadline: next.dueDate ? next.dueDate.toLocaleDateString() : "",
			status: "ongoing",
			source: "patient",
			// progress intentionally undefined per D-32
		};
		setPatientTasks((prev) => [newTask, ...prev]);
	};

	const [bookingTask, setBookingTask] = useState<JourneyTask | null>(null);

	// CONTEXT D-04, D-20..D-27 — analytics infrastructure (parent owns analytics
	// per D-28 so child components stay reusable / TASK-10 gate intact).
	const { logEvent } = useAnalytics();

	// CONTEXT D-20 — page view fires once on mount; stepId is the initial selectedStep.
	useTrackPageView("journey_page_viewed", {
		stepId: effectiveSelectedId,
	});

	// CONTEXT D-21 — wrap step selector to log selection events.
	const handleSelectStep = useCallback(
		(nextStepId: string) => {
			logEvent("journey_step_selected", undefined, {
				stepId: nextStepId,
				prevStepId: selectedStepId ?? "",
			});
			setSelectedStepId(nextStepId);
		},
		[logEvent, selectedStepId],
	);

	const handleTaskOpen = useCallback(
		(task: JourneyTask) => {
			logEvent("journey_task_opened", undefined, {
				taskId: task.id,
				taskType: task.iconName,
				stepId: effectiveSelectedId,
			});
			if (task.iconName === "book") {
				setBookingTask(task);
			}
		},
		[logEvent, effectiveSelectedId],
	);

	const handleBookingComplete = useCallback(
		(_meeting: ScheduledMeeting) => {
			logEvent("journey_booking_complete", undefined, {
				stepId: effectiveSelectedId,
			});
		},
		[logEvent, effectiveSelectedId],
	);

	return (
		<>
			<Header />
			<main className={styles.page} dir={direction}>
				{showSkeleton && <JourneyShellSkeleton />}

				{isError && (
					<FullPageErrorState
						titleKey="errors.loadFailed"
						descriptionKey="errors.loadFailedDescription"
						retryKey="errors.retry"
						onRetry={() => refetch()}
						t={t}
					/>
				)}

				{!showSkeleton && !isError && journey && (
					<>
						<JourneyHeader
							journey={journey}
							currentStepIndex={currentStepIndex}
							totalSteps={journey.steps.length}
						/>

						<StepPills
							steps={journey.steps}
							selectedStepId={effectiveSelectedId}
							onSelectStep={handleSelectStep}
						/>

						{/* Phase 41 — Step Content + Action Buttons (CONT-01..03, ACT-01, ACT-02) */}
						<StepDetail step={selectedStep} />
						<StepHighlights step={selectedStep} />
						<StepActionButtons
							step={selectedStep}
							onAnalyticsEvent={(action) => {
								if (action === "ask_eve") {
									logEvent("journey_ask_eve", undefined, {
										source: "page",
										stepId: effectiveSelectedId,
									});
								} else {
									logEvent("journey_nurse_tapped", undefined, {
										source: "page",
										stepId: effectiveSelectedId,
									});
								}
							}}
						/>

						{/* Phase 42 — Current Tasks carousel (TASK-01..09) */}
						<CurrentTasks
							tasks={mergedTasks}
							onAddTask={handleAddTask}
							onTaskOpen={handleTaskOpen}
							onFilterChange={(filter) => {
								logEvent("journey_task_toggled", undefined, {
									filter,
									stepId: effectiveSelectedId,
								});
							}}
							onAskEveAnalytics={(task) => {
								logEvent("journey_ask_eve", undefined, {
									source: "task",
									stepId: effectiveSelectedId,
									taskId: task.id,
								});
							}}
						/>

						{bookingTask && (
							<JourneyBookingModal
								open={!!bookingTask}
								task={bookingTask}
								journeyType={treatmentType}
								locale={locale}
								patientUserId={user?.id ?? ""}
								providerId={assignedProviderId}
								onClose={() => setBookingTask(null)}
								onBookingComplete={handleBookingComplete}
							/>
						)}

						{/* Phase 43 — Common Questions accordion + Ask Eve / Call Digital Nurse buttons (FAQ-01..04, ANLY-01) */}
						<CommonQuestions
							faqs={selectedStep?.faqs ?? []}
							step={selectedStep}
							stepId={effectiveSelectedId}
							onFaqToggle={(faqId, expanded) => {
								logEvent("journey_faq_toggled", undefined, {
									faqId,
									expanded: String(expanded),
									stepId: effectiveSelectedId,
								});
							}}
							onAskEve={(prefill) => {
								logEvent("journey_ask_eve", undefined, {
									source: "faq",
									stepId: effectiveSelectedId,
								});
								// Default Ask Eve behavior: set prefill + navigate to /chat.
								// StepActionButtons' default is bypassed when onAskEve is supplied,
								// so we re-implement the default here (CONTEXT D-08).
								setPrefillQuestion(prefill);
								navigate("/chat");
							}}
							onCallNurse={() => {
								logEvent("journey_nurse_tapped", undefined, {
									source: "faq",
									stepId: effectiveSelectedId,
								});
								// Default Call Digital Nurse behavior: toast (CONTEXT D-09).
								toast.success(t("callDigitalNurse.comingSoon"));
							}}
						/>
					</>
				)}
			</main>
		</>
	);
}
